/**
 * Which usages a CRD lookup inside a response reads and writes (SR-416, the member lookup design
 * ML §3, FSP content design §7).
 *
 * **A mirror of survey-api's `MemberLookupShapes` and `RespondentLookupShapes`**
 * (`Application/Responses/`), and moves with them: nothing on the wire says which repeat group a
 * lookup serves or which usage holds its CRD, and survey-api calls no lookup itself — the client
 * does, after the flush that wrote the CRD (ML §6). So this table is read **only to decide when
 * to call**, never what is visible or required: the U4 follow-up a lookup opens is the engine's
 * `usageVisibilityByInstance`, and the names it proposes are answers like any other.
 *
 * A shape applies to a definition carrying its usages — survey-api's own rule ("a version carrying
 * every usage of a shape is served by it"), so a July definition, which carries none of them,
 * calls no lookup.
 */

export interface IMemberLookupShape {
  /** The repeat group whose rows are looked up. */
  group: string;
  /** The row's CRD (`int`). */
  crd: string;
  /** "Is this member registered?" — the radio row 1's lookup proposes `registeredYes` into. */
  registered: string;
  registeredYes: string;
  /** The names the lookup proposes — `confirm` on Teams, `locked_if_filled` on RIA. */
  firstName: string;
  lastName: string;
  /** The U4 follow-up the lookup's pin opens in the row (ML §8 names it after the member). */
  u4: string;
  /**
   * Row 1 is the lead's (Teams; survey-api's `MemberLookupShape.LeadRow`): with a blank CRD it
   * takes the lead's, and the radio `YES`, on that row's lookup, and while it holds them the two
   * are locked — read off `IAnswer.locked`, never judged here. A firm has no lead, so its blank
   * row 1 answers `crd_missing` and is never looked up here.
   */
  leadRow: boolean;
}

export const MEMBER_LOOKUPS: readonly IMemberLookupShape[] = [
  {
    group: 'TEAM-TM-001/members',
    crd: 'TEAM-TM-003',
    registered: 'TEAM-TM-002',
    registeredYes: 'YES',
    firstName: 'TEAM-TM-004',
    lastName: 'TEAM-TM-005',
    u4: 'TEAM-TM-013',
    leadRow: true
  },
  {
    group: 'RIA-PC-001/principals',
    crd: 'RIA-PC-003',
    registered: 'RIA-PC-002',
    registeredYes: 'YES',
    firstName: 'RIA-PC-004',
    lastName: 'RIA-PC-005',
    u4: 'RIA-PC-013',
    leadRow: false
  }
];

export interface IRespondentLookupShape {
  /** The respondent's own CRD, at the response's root. */
  crd: string;
  firstName: string;
  lastName: string;
  /** BrokerCheck's firm (`locked_if_filled`, shown once the person is pinned). */
  brokerDealer: string;
  /** IAPD's firm. */
  riaFirmName: string;
}

export const RESPONDENT_LOOKUPS: readonly IRespondentLookupShape[] = [
  {
    crd: 'FSP-RG-003',
    firstName: 'FSP-AP-001',
    lastName: 'FSP-AP-002',
    brokerDealer: 'FSP-RG-004',
    riaFirmName: 'FSP-RG-005'
  }
];

/** The member shapes a definition carries every usage of. */
export function memberShapesOf(usageCodes: ReadonlySet<string>): IMemberLookupShape[] {
  return MEMBER_LOOKUPS.filter((shape) =>
    carriesAll(usageCodes, [
      shape.group,
      shape.crd,
      shape.registered,
      shape.firstName,
      shape.lastName
    ])
  );
}

/** The respondent shape a definition carries every usage of, or null. */
export function respondentShapeOf(
  usageCodes: ReadonlySet<string>
): Nullable<IRespondentLookupShape> {
  return (
    RESPONDENT_LOOKUPS.find((shape) =>
      carriesAll(usageCodes, [
        shape.crd,
        shape.firstName,
        shape.lastName,
        shape.brokerDealer,
        shape.riaFirmName
      ])
    ) ?? null
  );
}

function carriesAll(usageCodes: ReadonlySet<string>, codes: string[]): boolean {
  return codes.every((code) => usageCodes.has(code));
}
